import React, { FC, useMemo } from 'react';
import {
  FormControl,
  FormControlProps,
  FormHelperText,
  MenuItem,
  Select,
  Theme,
  styled,
} from '@material-ui/core';
import { CustomInput, CustomInputLabel } from './CustomForm';
import { uniqBy } from 'lodash';

export type FilterOption = {
  id: string;
  label: string;
};

const NewCustomFormControl = styled(FormControl)<Theme>(({ theme }) => ({
  background: theme.palette.common.white,
  border: theme.config.defaultBorder(),
  borderRadius: theme.config.defaultBorderRadius,
  padding: theme.spacing(1, 2),
  fontSize: theme.typography.h3.fontSize,
  color: theme.palette.grey['900'],
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  opacity: 1,
  maxWidth: '50%',
  '&.disabled': {
    opacity: 0.5,
  },
}));

type FilterProps = Omit<
  FormControlProps,
  'id' | 'label' | 'value' | 'onChange' | 'options' | 'emptyOption' | 'disabled' | 'placeholder'
> & {
  id: string;
  label: string;
  value?: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  emptyOption?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
};

const SimpleFilter: FC<FilterProps> = ({
  id,
  label,
  value = '',
  onChange,
  options,
  emptyOption,
  placeholder,
  helperText,
  disabled,
  ...props
}) => {
  const labelId = useMemo(() => `${id}-label`, [id]);
  const filteredOptions = uniqBy(options, ({ id }) => id);

  return (
    <NewCustomFormControl className={`filter ${disabled ? 'disabled' : ''}`} {...props}>
      <CustomInputLabel id={labelId}>{label}</CustomInputLabel>
      <Select
        id={id}
        labelId={labelId}
        input={<CustomInput />}
        value={value}
        onChange={(event) => onChange(event.target.value as string)}
        displayEmpty={emptyOption}
      >
        {emptyOption && <MenuItem value="">{placeholder || ' '}</MenuItem>}
        {filteredOptions.map(({ id: optionId, label }) => (
          <MenuItem key={optionId} value={optionId}>
            {label}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </NewCustomFormControl>
  );
};

export default SimpleFilter;
